import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import AppHeader from "@/components/AppHeader";

const TYPE_LABEL = {
  payment: "Account activation",
  deposit: "Deposit",
  withdrawal_requested: "Withdrawal request",
  withdrawal_executed: "Withdrawal executed",
  withdrawal_cancelled: "Withdrawal cancelled",
  timelock_created: "Time-lock created",
  timelock_withdrawn: "Time-lock released",
  banking_account_created: "Bank account opened",
  banking_card_issued: "Debit card issued",
  banking_provisioned: "Banking accounts provisioned",
  timelock_to_spending: "Time-lock → Spending",
};

export default function Activity() {
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/transactions").then((r) => { setTx(r.data); setLoading(false); });
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Activity</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">A quiet ledger.</h1>
        <p className="text-moss mt-3 max-w-2xl text-sm sm:text-base">Every movement, dated and traceable.</p>

        <div className="mt-8 sm:mt-12 bg-linen border border-sand rounded-2xl overflow-hidden" data-testid="activity-table">
          {loading ? (
            <div className="p-12 font-mono text-moss">loading…</div>
          ) : tx.length === 0 ? (
            <div className="p-12 text-center text-sage" data-testid="activity-empty">No activity yet.</div>
          ) : (
            <>
              {/* Desktop table (sm+) */}
              <div className="hidden sm:block divide-y divide-sand">
                <div className="grid grid-cols-12 px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-sage">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-4">Description</div>
                  <div className="col-span-3">Type</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {tx.map((t) => (
                  <div key={t.id} className="grid grid-cols-12 px-6 py-4 items-center text-sm" data-testid={`tx-${t.id}`}>
                    <div className="col-span-3 font-mono text-xs text-moss">
                      {new Date(t.created_at).toLocaleString()}
                    </div>
                    <div className="col-span-4 text-ink break-words">
                      {t.description}
                      {t.transfer_id && (
                        <div className="font-mono text-[10px] text-forest mt-0.5 break-all">↳ Transfer: {t.transfer_id}</div>
                      )}
                      {t.settlement_error && (
                        <div className="text-[10px] text-gold mt-0.5">⚠ Settlement pending: {t.settlement_error}</div>
                      )}
                    </div>
                    <div className="col-span-3 text-moss flex items-center gap-2 flex-wrap">
                      <span>{TYPE_LABEL[t.type] || t.type}</span>
                      {t.settled && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-forest text-white">settled</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right font-mono text-ink">{fmtMoney(t.amount)}</div>
                  </div>
                ))}
              </div>

              {/* Mobile stacked cards */}
              <ul className="sm:hidden divide-y divide-sand" data-testid="activity-list-mobile">
                {tx.map((t) => (
                  <li key={t.id} className="px-4 py-4" data-testid={`tx-${t.id}-mobile`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-ink leading-snug break-words">{t.description}</div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-sage">
                            {TYPE_LABEL[t.type] || t.type}
                          </span>
                          {t.settled && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-forest text-white">settled</span>
                          )}
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-sage">
                          {new Date(t.created_at).toLocaleString()}
                        </div>
                        {t.transfer_id && (
                          <div className="font-mono text-[10px] text-forest mt-1 break-all">↳ {t.transfer_id}</div>
                        )}
                        {t.settlement_error && (
                          <div className="text-[10px] text-gold mt-1 break-words">⚠ {t.settlement_error}</div>
                        )}
                      </div>
                      <div className="text-right font-mono text-sm text-ink shrink-0">
                        {fmtMoney(t.amount)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
